import React, { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { BookOpen, Lightbulb, Clock, Target, CheckCircle, AlertTriangle, Zap, Brain } from 'lucide-react';

interface Recipe {
  materials: string[];
  tools: string[];
  steps: {
    stepNumber: number;
    instruction: string;
    details: string;
    measurements: string[];
    checkpoints: string[];
  }[];
  commonMistakes: {
    mistake: string;
    solution: string;
  }[];
  tips: string[];
}

interface Insight {
  coreprinciples: {
    principle: string;
    explanation: string;
    importance: string;
  }[];
  creatorIntent: {
    technique: string;
    reason: string;
    impact: string;
  }[];
  beginnerAdvice: {
    commonPitfall: string;
    advice: string;
    improvement: string;
  }[];
  expertTips: string[];
  backgroundKnowledge: string[];
}

interface VideoInsightAnalysis {
  title: string;
  description: string;
  recipe: Recipe;
  insight: Insight;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedTime: string;
  skillsLearned: string[];
}

interface InsightViewProps {
  analysis: VideoInsightAnalysis;
  videoId: string;
}

export function InsightView({ analysis, videoId }: InsightViewProps) {
  const [activeView, setActiveView] = useState<'recipe' | 'insight'>('recipe');

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return 'bg-green-100 text-green-800';
      case 'intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'advanced': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDifficultyText = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner': return '초급';
      case 'intermediate': return '중급';
      case 'advanced': return '고급';
      default: return '미분류';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className={getDifficultyColor(analysis.difficulty)}>
              {getDifficultyText(analysis.difficulty)}
            </Badge>
            <Badge variant="secondary">
              <Clock className="w-3 h-3 mr-1" />
              {analysis.estimatedTime}
            </Badge>
            <Badge variant="outline">
              {analysis.category}
            </Badge>
          </div>
          
          <div>
            <h2 className="text-2xl font-bold mb-2">{analysis.title}</h2>
            <p className="text-muted-foreground">{analysis.description}</p>
          </div>

          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="font-semibold mb-2 flex items-center gap-2">
              <Target className="w-4 h-4" />
              학습 목표
            </h3>
            <div className="flex flex-wrap gap-2">
              {analysis.skillsLearned.map((skill, index) => (
                <Badge key={index} variant="secondary" className="text-xs">
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* View Selection */}
      <div className="flex gap-4">
        <Button
          variant={activeView === 'recipe' ? 'default' : 'outline'}
          onClick={() => setActiveView('recipe')}
          className="flex-1"
        >
          <BookOpen className="w-4 h-4 mr-2" />
          실무 레시피
        </Button>
        <Button
          variant={activeView === 'insight' ? 'default' : 'outline'}
          onClick={() => setActiveView('insight')}
          className="flex-1"
        >
          <Lightbulb className="w-4 h-4 mr-2" />
          핵심 인사이트
        </Button>
      </div>

      {/* Recipe View */}
      {activeView === 'recipe' && (
        <div className="space-y-6">
          {/* Materials & Tools */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                📦 준비물
              </h3>
              <ul className="space-y-2">
                {analysis.recipe.materials.map((material, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" />
                    <span className="text-sm">{material}</span>
                  </li>
                ))}
              </ul>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                🔧 도구
              </h3>
              <ul className="space-y-2">
                {analysis.recipe.tools.map((tool, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                    <span className="text-sm">{tool}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Steps */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              📋 제작 과정
            </h3>
            <div className="space-y-6">
              {analysis.recipe.steps.map((step, index) => (
                <div key={index} className="border-l-4 border-primary/20 pl-6 relative">
                  <div className="absolute -left-3 top-0 bg-primary text-primary-foreground rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold">
                    {step.stepNumber}
                  </div>
                  
                  <div className="space-y-3">
                    <h4 className="font-medium">{step.instruction}</h4>
                    <p className="text-sm text-muted-foreground">{step.details}</p>
                    
                    {step.measurements.length > 0 && (
                      <div className="bg-blue-50 rounded-lg p-3">
                        <h5 className="font-medium text-sm mb-2">📏 정량 데이터</h5>
                        <ul className="text-sm space-y-1">
                          {step.measurements.map((measurement, idx) => (
                            <li key={idx}>• {measurement}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {step.checkpoints.length > 0 && (
                      <div className="bg-green-50 rounded-lg p-3">
                        <h5 className="font-medium text-sm mb-2">✅ 체크포인트</h5>
                        <ul className="text-sm space-y-1">
                          {step.checkpoints.map((checkpoint, idx) => (
                            <li key={idx}>• {checkpoint}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Common Mistakes & Tips */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-red-500" />
                자주 하는 실수
              </h3>
              <div className="space-y-4">
                {analysis.recipe.commonMistakes.map((mistake, index) => (
                  <div key={index} className="bg-red-50 rounded-lg p-4">
                    <h4 className="font-medium text-sm text-red-800 mb-2">❌ {mistake.mistake}</h4>
                    <p className="text-sm text-red-700">💡 {mistake.solution}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-500" />
                성공 팁
              </h3>
              <div className="space-y-3">
                {analysis.recipe.tips.map((tip, index) => (
                  <div key={index} className="bg-yellow-50 rounded-lg p-3">
                    <p className="text-sm">💡 {tip}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Insight View */}
      {activeView === 'insight' && (
        <div className="space-y-6">
          {/* Core Principles */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Brain className="w-4 h-4 text-purple-500" />
              핵심 원리
            </h3>
            <div className="space-y-4">
              {analysis.insight.coreprinciples.map((principle, index) => (
                <div key={index} className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-medium text-purple-800 mb-2">{principle.principle}</h4>
                  <p className="text-sm text-gray-700 mb-2">{principle.explanation}</p>
                  <p className="text-sm text-purple-700 font-medium">🎯 {principle.importance}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Creator Intent */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              🎨 제작자의 의도
            </h3>
            <div className="space-y-4">
              {analysis.insight.creatorIntent.map((intent, index) => (
                <div key={index} className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">{intent.technique}</h4>
                  <p className="text-sm text-gray-700 mb-2">🤔 이유: {intent.reason}</p>
                  <p className="text-sm text-blue-700">📈 영향: {intent.impact}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Beginner Advice */}
          <Card className="p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              🌱 초보자를 위한 조언
            </h3>
            <div className="space-y-4">
              {analysis.insight.beginnerAdvice.map((advice, index) => (
                <div key={index} className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-medium text-red-600 mb-2">⚠️ {advice.commonPitfall}</h4>
                  <p className="text-sm text-gray-700 mb-2">💡 해결책: {advice.advice}</p>
                  <p className="text-sm text-green-700">🚀 개선: {advice.improvement}</p>
                </div>
              ))}
            </div>
          </Card>

          {/* Expert Tips & Background Knowledge */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                🏆 고급 활용법
              </h3>
              <div className="space-y-3">
                {analysis.insight.expertTips.map((tip, index) => (
                  <div key={index} className="bg-orange-50 rounded-lg p-3">
                    <p className="text-sm">⚡ {tip}</p>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                📚 배경 지식
              </h3>
              <div className="space-y-3">
                {analysis.insight.backgroundKnowledge.map((knowledge, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm">📖 {knowledge}</p>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
